import { Collapse } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { type EventApplicationModuleSpecsFragment } from '../../../../api/fragments/EventApplicationModuleSpecs';
import { type StandardApplicationModuleSpecsFragment } from '../../../../api/fragments/StandardApplicationModuleSpecs';
import { useGetLocalVariantsOptionsQuery } from '../../../../api/queries/getLocalVariantsOptions';
import { useListDealersQuery } from '../../../../api/queries/listDealers';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import TransferField from '../../../../components/fields/TransferField';
import LoadingElement from '../../../../components/LoadingElement';
import CollapsibleWrapper from '../../../../components/wrappers/CollapsibleWrapper';
import useTranslatedString from '../../../../utilities/useTranslatedString';

type DealerVehiclesTransferFieldProps = {
    titles: ReactNode[];
    module: EventApplicationModuleSpecsFragment | StandardApplicationModuleSpecsFragment;
};

const DealerVehicleSections = ({ titles, module }: DealerVehiclesTransferFieldProps) => {
    const translatedString = useTranslatedString();

    const { dealerIds } = useMultipleDealerIds();

    const { data: dealerData, loading: dealerLoading } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: module.company.id,
            },
        },
    });

    const { data: localVariantData, loading: localVariantLoading } = useGetLocalVariantsOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                moduleId: module.vehicleModuleId,
            },
        },
    });

    const dataSources = useMemo(() => {
        if (dealerLoading || localVariantLoading) {
            return [];
        }

        return dealerData?.page.items
            .filter(dealer => dealerIds.includes(dealer.id))
            .map(dealer => ({
                key: dealer.id,
                title: translatedString(dealer.legalName),
                dataSource: localVariantData?.list.items.map(variant => ({
                    key: variant.versioning.suiteId,
                    title: translatedString(variant.name),
                })),
            }));
    }, [dealerData, dealerIds, dealerLoading, localVariantData, localVariantLoading, translatedString]);

    if (dealerLoading || localVariantLoading) {
        return <LoadingElement />;
    }

    return (
        <CollapsibleWrapper>
            {dataSources.map(({ key, title, dataSource }) => (
                <Collapse.Panel key={key} header={title}>
                    <TransferField
                        dataSource={dataSource}
                        listStyle={{
                            width: 400,
                            height: 200,
                        }}
                        name={`dealerVehicles.${key}`}
                        render={item => item.title}
                        titles={titles}
                    />
                </Collapse.Panel>
            ))}
        </CollapsibleWrapper>
    );
};

export default DealerVehicleSections;
